/**
 * @file        src/pages/hotel/AddDiscount.jsx
 * @description Create / edit a B2C hotel discount (`hotel_discounts`).
 *              Reused for /hotel/discounts/new and /hotel/discounts/:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-08-03 Claude — Required Brand field (registry code, no default) —
 *                          the API now demands `brand` on create.
 *   - 2026-08-10 Claude — discount_value must be > 0. The server also rejects a
 *                          duplicate rule at the same Min Booking Amount (409) —
 *                          surfaced via the existing error toast.
 *   - 2026-08-10 Claude — Valid To can no longer be a past date, and can no
 *                          longer precede Valid From. Console-wide date rule
 *                          pass (src/lib/dateRules.js), mirrored server-side.
 *   - 2026-08-10 Claude — Added "Applies To" (All/B2C/B2B — `applicable_role`,
 *                          added to the table by migration 026 but never
 *                          exposed here) so a discount can be scoped to B2B and
 *                          show up on the new Corporate Hotel Discount List.
 *   - 2026-09-12 Claude — Removed the "Applies To" input; the LIST that sent you
 *                          here decides it, arriving as `?audience=b2b`. This
 *                          form is shared by the B2C and Corporate Hotel
 *                          Discount lists and hardcoded 'all', so a discount
 *                          added from the Corporate list also applied to retail
 *                          (and vice versa). Editing keeps the row's OWN role.
 *   - 2026-09-15 Claude — Discount Value is now bounded on BOTH sides by the
 *                          shared rule (lib/discountRules.js): > 0 (already
 *                          true) and <= 100 when Discount Type is Percentage.
 *                          "500%" used to save and then get silently clamped to
 *                          the fare at checkout. The Max hint on the input
 *                          tracks the selected type.
 *   - 2026-09-15 Claude — Added the one-line note stating HOW a discount reaches
 *                          the bill: automatically, best-value rule only, off the
 *                          ROOM TOTAL — the convenience fee is added after and is
 *                          never discounted, which is the opposite of flights and
 *                          is not inferable from this list.
 *   - 2026-10-06 Claude — Domestic / International stays (migration 076), judged
 *                          on the property's country. Blank = both.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import { HOTEL_DISCOUNT_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import BrandSelect from '../../components/BrandSelect';
import { get, post, patch } from '../../api/client';
import { toast } from '../../lib/toast';
import { todayStr, laterDate } from '../../lib/dateRules';
import { discountValueError, discountValueMax } from '../../lib/discountRules';
import { DOM_INT_SELECT_OPTIONS } from '../../lib/domInt';

const dateish = z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'), z.literal('')]).optional();
const schema = z.object({
  brand: z.string().trim().min(1, 'Required'),
  name: z.string().trim().min(1, 'Required').max(160),
  discount_type: z.enum(['percentage', 'flat']),
  discount_value: z.coerce.number().min(0.01, 'Must be greater than 0'),  // the <= 100% half is the cross-field refine below
  min_booking_amount: z.coerce.number().min(0).optional(),
  max_discount: z.coerce.number().min(0).optional(),
  valid_from: dateish,
  valid_to: dateish,
  applicable_role: z.enum(['b2c', 'b2b']),
  is_active: z.boolean(),
}).refine((d) => !discountValueError(d.discount_type, d.discount_value), {
  // Percentage and value are only meaningful together: 500 is a fine flat
  // discount and a nonsensical percentage one.
  message: 'A percentage discount cannot exceed 100%.',
  path: ['discount_value'],
}).refine((d) => !d.valid_to || d.valid_to >= todayStr(), {
  message: 'Valid To cannot be in the past.',
  path: ['valid_to'],
}).refine((d) => !d.valid_from || !d.valid_to || d.valid_to >= d.valid_from, {
  message: 'Valid To cannot be before Valid From.',
  path: ['valid_to'],
});

// `applicable_role` is NOT a constant here: it is whichever list opened this
// form (see blankForm). Hardcoding 'all' is what let a corporate discount price
// retail bookings.
const empty = {
  brand: '', name: '', discount_type: 'percentage', discount_value: '',
  min_booking_amount: '', max_discount: '', valid_from: '', valid_to: '', dom_int: '', is_active: true,
};
const blankForm = (role) => ({ ...empty, applicable_role: role });

// DATE columns arrive as ISO datetime strings; date inputs need YYYY-MM-DD.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

export default function AddDiscount() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  // Which list opened this form. Only ever sets the NEW row's role — the value
  // is still validated against the enum server-side.
  const audience = searchParams.get('audience') === 'b2b' ? 'b2b' : 'b2c';

  const [form, setForm] = useState(() => blankForm(audience));
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: ['hotel-discounts', 'one', id],
    queryFn: () => get(`/hotel-discounts/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const d = existing.data;
      setForm({
        brand: d.brand || '',
        name: d.name || '',
        discount_type: d.discount_type || 'percentage',
        discount_value: d.discount_value ?? '',
        min_booking_amount: d.min_booking_amount ?? '',
        max_discount: d.max_discount ?? '',
        valid_from: toDateInput(d.valid_from),
        valid_to: toDateInput(d.valid_to),
        dom_int: d.dom_int || '',
        // Never 'all': the wildcard is retired (admin migration 055) and the
        // API now rejects it, so an unmigrated legacy row re-scopes to THIS
        // screen's audience on edit rather than failing to save.
        applicable_role: d.applicable_role === 'b2b' ? 'b2b' : audience,
        is_active: !!d.is_active,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/hotel-discounts/${id}`, body) : post('/hotel-discounts', body)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Discount updated' : 'Discount added', `${form.name} saved.`);
      qc.invalidateQueries({ queryKey: ['hotel-discounts'] });
      navigate('/hotel/discounts');
    },
    onError: (err) => {
      const fields = err.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const candidate = {
      ...form,
      min_booking_amount: form.min_booking_amount === '' ? undefined : form.min_booking_amount,
      max_discount: form.max_discount === '' ? undefined : form.max_discount,
    };
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
      const fieldErrs = {};
      for (const i of parsed.error.issues) fieldErrs[i.path.join('.')] = i.message;
      return setErrors(fieldErrs);
    }
    setErrors({});
    const body = { ...parsed.data };
    if (!body.valid_from) delete body.valid_from;
    if (!body.valid_to) delete body.valid_to;
    // Explicit null = both, so choosing All on edit clears a stored scope.
    body.dom_int = form.dom_int || null;
    mut.mutate(body);
  }

  function onChange(key) {
    return (e) => setForm({ ...form, [key]: e.target.value });
  }

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Hotel Discount' : 'Add Hotel Discount'}
        subtitle="B2C Hotel Management"
        actions={<button className="btn ghost" onClick={() => navigate('/hotel/discounts')}><Icon name="x" /> Cancel</button>}
      />

      <div className="notice-banner info">{HOTEL_DISCOUNT_NOTE}</div>

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Brand" required err={errors.brand}>
            <BrandSelect value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} invalid={!!errors.brand} />
          </Field>
          <Field label="Discount Name" required err={errors.name}>
            <input value={form.name} onChange={onChange('name')} maxLength={160} placeholder="e.g. Summer Hotel Sale" />
          </Field>
          <Field label="Discount Type" required err={errors.discount_type}>
            <Select
              value={form.discount_type}
              onChange={(v) => setForm({ ...form, discount_type: v })}
              invalid={!!errors.discount_type}
              options={[{ value: 'percentage', label: 'Percentage' }, { value: 'flat', label: 'Flat' }]}
            />
          </Field>
          <Field label="Discount Value" required err={errors.discount_value}>
            <input type="number" min="0.01" max={discountValueMax(form.discount_type)} step="0.01" value={form.discount_value} onChange={onChange('discount_value')} placeholder={form.discount_type === 'flat' ? 'e.g. 500' : 'e.g. 10 (max 100)'} />
          </Field>
          <Field label="Min Booking Amount" err={errors.min_booking_amount}>
            <input type="number" min="0" step="0.01" value={form.min_booking_amount} onChange={onChange('min_booking_amount')} />
          </Field>
          <Field label="Max Discount" err={errors.max_discount}>
            <input type="number" min="0" step="0.01" value={form.max_discount} onChange={onChange('max_discount')} />
          </Field>
          <Field label="Valid From" err={errors.valid_from}>
            <DatePicker value={form.valid_from} onChange={(v) => setForm({ ...form, valid_from: v })} invalid={!!errors.valid_from} />
          </Field>
          <Field label="Valid To" err={errors.valid_to}>
            <DatePicker
              value={form.valid_to}
              onChange={(v) => setForm({ ...form, valid_to: v })}
              invalid={!!errors.valid_to}
              min={laterDate(todayStr(), form.valid_from)}
            />
          </Field>
          <Field label="Domestic / International">
            <Select
              value={form.dom_int}
              onChange={(v) => setForm({ ...form, dom_int: v })}
              options={DOM_INT_SELECT_OPTIONS}
            />
          </Field>

          <Field label="Status">
            <label className="check">
              <input type="checkbox" checked={form.is_active}
                     onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active</span>
            </label>
          </Field>

          <div className="form-actions">
            {!isEdit && (
              <button type="button" className="btn ghost" onClick={() => setForm(empty)}>Reset</button>
            )}
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Discount'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
